import React, { useState, useEffect, useCallback } from "react";
import { X, Download, Share, Smartphone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trackEvent } from "@/utils/analytics";

// Detect platform
const getPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // iOS detection
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return "ios";
  }
  
  // Android detection
  if (/android/i.test(ua)) {
    return "android";
  }
  
  return "desktop";
};

// Check if running in standalone mode (already installed)
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// Check if Safari on iOS
const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
};

// Check if iOS but not Safari
const isIOSNotSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isNotSafari = /CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && isNotSafari;
};

// Storage keys
const STORAGE_KEYS = {
  DISMISSED: "a2hs_dismissed",
  DISMISS_COUNT: "a2hs_dismiss_count",
  LAST_SHOWN: "a2hs_last_shown"
};

const MAX_DISMISS_COUNT = 2;

/**
 * Add to Home Screen Helper Component
 * Shows a non-intrusive banner after the user's first successful call log
 */
const AddToHomeScreen = ({ show, onDismiss }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState("desktop");

  // Check if we should show based on dismissal history
  const shouldShow = useCallback(() => {
    // Don't show if already installed as PWA
    if (isStandalone()) return false;
    
    // Check permanent dismissal
    const dismissed = localStorage.getItem(STORAGE_KEYS.DISMISSED) === "true";
    if (dismissed) return false;
    
    // Check dismiss count (max 2 times)
    const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.DISMISS_COUNT) || "0");
    if (dismissCount >= MAX_DISMISS_COUNT) return false;
    
    // Don't show again within 24 hours of last dismissal
    const lastShown = parseInt(localStorage.getItem(STORAGE_KEYS.LAST_SHOWN) || "0");
    const dayInMs = 24 * 60 * 60 * 1000;
    if (Date.now() - lastShown < dayInMs && dismissCount > 0) return false;
    
    return true;
  }, []);

  // Listen for beforeinstallprompt event (Android Chrome)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log("[A2HS] Install prompt captured");
    };
    
    window.addEventListener("beforeinstallprompt", handler);
    
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Set platform on mount
  useEffect(() => {
    setPlatform(getPlatform());
  }, []);

  // Show banner when triggered and conditions are met
  useEffect(() => {
    if (show && shouldShow()) {
      setShowBanner(true);
      localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, Date.now().toString());
      
      // Track event
      trackEvent("a2hs_prompt_shown", { platform, path: window.location.pathname });
    }
  }, [show, shouldShow, platform]);

  // Handle dismissal
  const handleDismiss = (permanent = false) => {
    setShowBanner(false);
    
    if (permanent) {
      localStorage.setItem(STORAGE_KEYS.DISMISSED, "true");
    } else {
      const count = parseInt(localStorage.getItem(STORAGE_KEYS.DISMISS_COUNT) || "0");
      localStorage.setItem(STORAGE_KEYS.DISMISS_COUNT, (count + 1).toString());
    }
    
    trackEvent("a2hs_prompt_dismissed", { platform, permanent });
    onDismiss?.();
  };

  // Handle install click
  const handleInstallClick = async () => {
    trackEvent("a2hs_install_clicked", { platform });
    
    // Android with native prompt
    if (platform === "android" && deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        trackEvent("a2hs_install_accepted", { platform, outcome });
        
        if (outcome === "accepted") {
          setShowBanner(false);
          localStorage.setItem(STORAGE_KEYS.DISMISSED, "true");
        }
        setDeferredPrompt(null);
      } catch (error) {
        console.error("[A2HS] Install prompt error:", error);
        setShowInstructions(true);
      }
    } else {
      // Show manual instructions
      setShowInstructions(true);
      
      if (!deferredPrompt && platform === "android") {
        trackEvent("a2hs_install_unavailable", { platform });
      }
    }
  };

  // Close instructions modal
  const handleCloseInstructions = () => {
    setShowInstructions(false);
    setShowBanner(false);
    handleDismiss(true); // Permanently dismiss after showing instructions
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Banner */}
      <div 
        className="bg-gradient-to-r from-[#E07A5F] to-[#D0694E] text-white px-4 py-3 shadow-lg"
        data-testid="a2hs-banner"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm sm:text-base">Want faster access?</p>
              <p className="text-xs sm:text-sm text-white/80 truncate">
                Add OuttaTouch to your Home Screen so it opens like an app.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleInstallClick}
              className="bg-white text-[#E07A5F] hover:bg-white/90 rounded-full text-xs sm:text-sm px-3 sm:px-4 py-2"
              data-testid="a2hs-install-btn"
            >
              <Download className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add to Home Screen</span>
              <span className="sm:hidden">Add</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDismiss(false)}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
              data-testid="a2hs-dismiss-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Instructions Modal */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#3D405B] text-center" style={{ fontFamily: 'Fraunces, serif' }}>
              Add OuttaTouch to your Home Screen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* iOS Safari Instructions */}
            {platform === "ios" && isIOSSafari() && (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap the <span className="font-semibold">Share</span> icon <Share className="w-4 h-4 inline text-[#007AFF]" /> at the bottom of Safari
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <p className="text-[#3D405B] text-sm">
                    Scroll down and tap <span className="font-semibold">"Add to Home Screen"</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap <span className="font-semibold">Add</span> in the top right corner
                  </p>
                </div>
                <p className="text-xs text-[#6D6F7C] text-center italic">
                  Tip: This makes OuttaTouch open like an app.
                </p>
              </div>
            )}

            {/* iOS Not Safari Warning */}
            {platform === "ios" && isIOSNotSafari() && (
              <div className="space-y-3">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-amber-800 text-sm font-medium text-center">
                    On iPhone, Add to Home Screen works best in Safari.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <p className="text-[#3D405B] text-sm">
                    Open this page in <span className="font-semibold">Safari</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap the <span className="font-semibold">Share</span> icon <Share className="w-4 h-4 inline text-[#007AFF]" />
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap <span className="font-semibold">"Add to Home Screen"</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap <span className="font-semibold">Add</span>
                  </p>
                </div>
              </div>
            )}

            {/* Android Instructions (manual fallback) */}
            {platform === "android" && (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap the <span className="font-semibold">⋮ menu</span> in Chrome (top right)
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap <span className="font-semibold">"Install app"</span> or <span className="font-semibold">"Add to Home screen"</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <p className="text-[#3D405B] text-sm">
                    Tap <span className="font-semibold">Add</span>
                  </p>
                </div>
              </div>
            )}

            {/* Desktop Instructions */}
            {platform === "desktop" && (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <p className="text-[#3D405B] text-sm">
                    Look for the <span className="font-semibold">install icon</span> <ExternalLink className="w-4 h-4 inline" /> in your browser's address bar
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                  <span className="w-6 h-6 bg-[#E07A5F] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <p className="text-[#3D405B] text-sm">
                    Click <span className="font-semibold">Install</span>
                  </p>
                </div>
                <p className="text-xs text-[#6D6F7C] text-center italic">
                  Works best in Chrome, Edge, or Safari.
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => handleDismiss(false)}
              className="flex-1 rounded-full border-[#F4F1EA]"
            >
              Not now
            </Button>
            <Button
              onClick={handleCloseInstructions}
              className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToHomeScreen;
